import { ref } from "vue";
import { defineStore } from "pinia";
import {
  askForMessage,
  isInAppMessagingEnabled,
  reportDeliveryEvent,
} from "@/services/inapp.service";

/**
 * IN_APP engagement modal state. Frequency discipline lives here:
 *   - at most ONE modal per browser session (in-memory latch ALWAYS + sessionStorage when it
 *     works; the server's global caps and one-per-ask are the backstop)
 *   - "shown" is reported (and the session latched) ONLY when the modal actually reaches the
 *     DOM — the rendered component calls markRendered(). A message fetched but never rendered
 *     (layout without PopupContainer, superseded navigation) is silently discarded; the server
 *     never stamped shownAt, so it can be served again later.
 *   - request generations: rapid view transitions supersede in-flight asks — a stale response
 *     for an earlier route is dropped, and the LATEST view's ask always runs.
 *   - never render a modal within 30s of its expiry (server refuses <60s at serve time).
 * Every failure is silent — engagement must never break the app.
 */
const SESSION_KEY = "as_inapp_modal_shown";
/** Never render with less than this much life left (server refuses <60s at serve time). */
const RENDER_EXPIRY_MARGIN_MS = 30_000;

export const useInAppModalStore = defineStore("inappModal", () => {
  /** the message currently offered to the UI (null = nothing) */
  const current = ref(null);
  /** true once the visible component confirmed the modal reached the DOM */
  const rendered = ref(false);
  /** in-memory one-per-session latch — authoritative even when sessionStorage fails */
  const memoryLatched = ref(false);
  /** monotonically increasing ask generation — only the latest may commit */
  let askGen = 0;

  const latched = () => {
    if (memoryLatched.value) return true;
    try {
      return sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      return false; // memory latch is the authority; storage is best-effort persistence
    }
  };
  const latch = () => {
    memoryLatched.value = true;
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* in-memory latch already set */
    }
  };

  /**
   * Called on every SUCCESSFUL view transition (router afterEach) with the target route name.
   * Asks the engine; a fresh answer is OFFERED to the UI (current) — rendering + the shown
   * report happen in the component via markRendered().
   */
  async function askForView(viewName) {
    if (!isInAppMessagingEnabled() || !viewName || latched()) return;
    if (current.value && rendered.value) return; // a modal is on screen
    current.value = null; // discard any unrendered leftover — it never reached the DOM
    const myGen = ++askGen;
    const message = await askForMessage(viewName);
    if (myGen !== askGen) return; // superseded by a later navigation — drop this response
    if (!message || latched()) return;
    // pre-render expiry re-check: a stale modal must never appear
    const expiresAt = Date.parse(message.expiresAt ?? "");
    if (
      !Number.isFinite(expiresAt) ||
      expiresAt - Date.now() < RENDER_EXPIRY_MARGIN_MS
    )
      return;
    rendered.value = false;
    current.value = message;
  }

  /** The visible component confirms the modal is actually in the DOM → latch + report shown. */
  function markRendered() {
    if (!current.value || rendered.value) return;
    rendered.value = true;
    latch();
    reportDeliveryEvent(current.value.deliveryId, "shown"); // fire-and-forget
  }

  /** Backdrop / close / "not now". */
  function dismiss() {
    const m = current.value;
    const wasRendered = rendered.value;
    current.value = null;
    rendered.value = false;
    if (m && wasRendered) reportDeliveryEvent(m.deliveryId, "dismissed");
  }

  /** CTA click — the caller navigates (ctaPath is app-relative by contract). */
  function convert() {
    const m = current.value;
    const wasRendered = rendered.value;
    current.value = null;
    rendered.value = false;
    if (m && wasRendered) reportDeliveryEvent(m.deliveryId, "converted");
    return m?.ctaPath ?? null;
  }

  return {
    current,
    rendered,
    memoryLatched,
    askForView,
    markRendered,
    dismiss,
    convert,
  };
});
